import { Box } from "@mui/material"
import { useState } from "react"
import { intl } from "src/internationalization/intl"
import { Button, Grid, Typography } from "src/lib"
import { TextInput } from "src/lib/components/basics/TextInput"
import {
    ClarificationAnswer,
    ClarificationQuestion,
    setSortingProducts,
} from "src/services/api/sorting"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { useMessageStore } from "src/store/messageStore"
import { useRankingStore } from "src/store/rankingStore"

interface ClarificationQuestionsProps {
    round: number
    questions: ClarificationQuestion[]
}

/** Valor sintético para a opção "Outro" — sempre disponível, independente do que o modelo mandou. */
const OTHER_OPTION_VALUE = "__other__"

const letterFor = (index: number) => String.fromCharCode(65 + index)

/**
 * Renderiza as perguntas de esclarecimento de uma rodada de ranking como uma lista
 * de múltipla escolha (cada opção em uma linha, com letra) + "Outro" sempre disponível
 * para resposta livre, e envia a resposta direto para o backend (step 2).
 */
const ClarificationQuestions = ({ round, questions }: ClarificationQuestionsProps) => {
    const sortingStep = useRankingStore((s: any) => s.sortingStep)
    const pendingClarification = useRankingStore((s: any) => s.pendingClarification)
    const sortingContext = useRankingStore((s: any) => s.sortingContext)
    const assetKey = useRankingStore((s: any) => s.assetKey)
    const asset = useRankingStore((s: any) => s.asset)
    const setSortingResponse = useRankingStore((s: any) => s.setSortingResponse)
    const setConfirmedRanking = useConfirmedRankingStore((s: any) => s.setConfirmedRanking)
    const addMessage = useMessageStore((s: any) => s.addMessage)
    const updateMessage = useMessageStore((s: any) => s.updateMessage)
    const isStreaming = useMessageStore((s: any) => s.isStreaming)
    const setIsStreaming = useMessageStore((s: any) => s.setIsStreaming)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const jsonContext = useMessageStore((s: any) => s.jsonContext)

    const [selected, setSelected] = useState<Record<number, string[]>>({})
    const [freeform, setFreeform] = useState<Record<number, string>>({})
    const [submitting, setSubmitting] = useState(false)

    // Só fica interativo enquanto essa é a rodada de esclarecimento pendente atual
    // (evita reabrir perguntas de rodadas antigas já respondidas no histórico do chat).
    const isActive = !submitting && !isStreaming && sortingStep === 2 && pendingClarification?.round === round
    if (!isActive) return null

    const market = assetKey ?? asset
    if (!market) return null

    const toggleOption = (questionIndex: number, value: string, multiSelect: boolean) => {
        setSelected((prev) => {
            const current = prev[questionIndex] ?? []
            if (multiSelect) {
                const next = current.includes(value)
                    ? current.filter((v) => v !== value)
                    : [...current, value]
                return { ...prev, [questionIndex]: next }
            }
            return { ...prev, [questionIndex]: current.includes(value) ? [] : [value] }
        })
    }

    const buildAnswers = (): ClarificationAnswer[] =>
        questions
            .map((question, index) => {
                const chosenValues = selected[index] ?? []
                const labels = chosenValues
                    .filter((v) => v !== OTHER_OPTION_VALUE)
                    .map((value) => question.options.find((o) => o.value === value)?.label ?? value)
                const freeText = chosenValues.includes(OTHER_OPTION_VALUE) ? (freeform[index] ?? "").trim() : ""
                const parts = [...labels, ...(freeText ? [freeText] : [])]
                if (!parts.length) return null
                return { header: question.header, answer: parts.join(", ") }
            })
            .filter((a): a is ClarificationAnswer => !!a)

    const submitAnswers = async (answers: ClarificationAnswer[]) => {
        if (!answers.length || submitting) return
        setSubmitting(true)
        setIsStreaming(true)
        addMessage({
            text: answers.map((a) => `${a.header}: ${a.answer}`).join("; "),
            answer: false,
            context: actualContext,
            jsonContext,
        })
        const assistantId = addMessage({
            text: "",
            answer: true,
            status: "streaming",
            streamStatus: intl("chatStreamingStarting"),
            context: actualContext,
            jsonContext,
        })
        try {
            const response = await setSortingProducts({
                market,
                step: 2,
                ranking_text: sortingContext?.ranking_text,
                clarification_round: round,
                clarification_answers: answers,
            })

            if (response.next_step === 3 && response.ranking_text) {
                setConfirmedRanking(market, {
                    ranking_text: response.ranking_text,
                    cypher_query: response.cypher_query,
                })
            }
            if (response.next_step === 1) {
                setSortingResponse(1, {
                    ranking_text: response.ranking_text,
                    cypher_query: response.cypher_query,
                })
            } else if (response.next_step === 2 && response.clarification) {
                setSortingResponse(
                    2,
                    { ranking_text: response.ranking_text },
                    { round: response.clarification.round, questions: response.clarification.questions }
                )
            } else {
                setSortingResponse(0)
            }

            updateMessage(assistantId, {
                text: response.natural_language_response ?? "",
                status: "done",
                streamStatus: undefined,
                jsonMessage: response,
            })
        } catch (error) {
            const msg = error instanceof Error ? error.message : String(error)
            updateMessage(assistantId, {
                text: msg || intl("rankingClarificationSubmitError"),
                status: "error",
                streamStatus: undefined,
            })
        } finally {
            setSubmitting(false)
            setIsStreaming(false)
        }
    }

    return (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {questions.map((question, index) => {
                const options = [
                    ...question.options,
                    { label: intl("rankingClarificationOtherOption"), value: OTHER_OPTION_VALUE },
                ]
                const chosenValues = selected[index] ?? []
                const otherChosen = chosenValues.includes(OTHER_OPTION_VALUE)

                return (
                    <Box key={`${question.header}-${index}`} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                            {question.header}
                        </Typography>
                        <Typography variant="body2">{question.question}</Typography>
                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                flexDirection: "column",
                                border: `1px solid ${(theme.palette as any).neutral.darker}`,
                                borderRadius: "6px",
                                overflow: "hidden",
                            })}
                        >
                            {options.map((option, optIndex) => {
                                const isChosen = chosenValues.includes(option.value)
                                const isOther = option.value === OTHER_OPTION_VALUE
                                return (
                                    <Box
                                        key={option.value}
                                        component="button"
                                        type="button"
                                        onClick={() => toggleOption(index, option.value, !!question.multi_select)}
                                        sx={(theme) => ({
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            width: "100%",
                                            textAlign: "left",
                                            font: "inherit",
                                            color: (theme.palette as any).neutral.lighter,
                                            border: "none",
                                            borderTop:
                                                optIndex > 0
                                                    ? `1px solid ${(theme.palette as any).neutral.darker}`
                                                    : "none",
                                            backgroundColor: isChosen
                                                ? (theme.palette as any).primary.darkest
                                                : "transparent",
                                            cursor: "pointer",
                                            py: 0.6,
                                            px: 0.9,
                                            "&:hover": {
                                                backgroundColor: isChosen
                                                    ? (theme.palette as any).primary.darkest
                                                    : (theme.palette as any).neutral.darker,
                                            },
                                            "&:focus-visible": {
                                                outline: `2px solid ${(theme.palette as any).primary.main}`,
                                                outlineOffset: "-2px",
                                            },
                                        })}
                                    >
                                        <Box
                                            sx={(theme) => ({
                                                flexShrink: 0,
                                                width: 18,
                                                height: 18,
                                                borderRadius: "4px",
                                                border: `1px solid ${
                                                    isChosen
                                                        ? (theme.palette as any).primary.main
                                                        : (theme.palette as any).neutral.dark
                                                }`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.6875rem",
                                                fontWeight: 700,
                                                color: isChosen
                                                    ? (theme.palette as any).primary.main
                                                    : (theme.palette as any).neutral.main,
                                            })}
                                        >
                                            {letterFor(optIndex)}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontStyle: isOther ? "italic" : "normal" }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                        {otherChosen && (
                            <TextInput
                                autoFocus
                                placeholder={intl("rankingClarificationFreeformPlaceholder")}
                                size="small"
                                value={freeform[index] ?? ""}
                                onChange={(e: any) =>
                                    setFreeform((prev) => ({ ...prev, [index]: e.target.value }))
                                }
                            />
                        )}
                    </Box>
                )
            })}
            <Grid container justifyContent="flex-end">
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={submitting || buildAnswers().length === 0}
                    onClick={() => submitAnswers(buildAnswers())}
                    sx={{ fontSize: "0.8125rem", minWidth: "auto", px: 1.5, boxShadow: "none" }}
                >
                    {intl("rankingClarificationConfirm")}
                </Button>
            </Grid>
        </Box>
    )
}

export { ClarificationQuestions }
